/**
 * Format Lark field values for document generation
 * Converts complex Lark API field types to string representations
 */

import { bitable, FieldType, type IFieldMeta, type IFieldProperty, type IRecord, type ICommonLinkFieldProperty } from '@lark-base-open/js-sdk'
import { sanitizeFieldName } from './fieldManager'

/**
 * Format a date according to Lark's DateFormatter pattern
 * Patterns: yyyy/MM/dd, yyyy-MM-dd, MM/dd/yyyy, dd/MM/yyyy, MM-dd, yyyy/MM/dd HH:mm, yyyy-MM-dd HH:mm
 */
function formatDateWithPattern(date: Date, pattern: string): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return pattern
    .replace('yyyy', String(year))
    .replace('MM', month)
    .replace('dd', day)
    .replace('HH', hours)
    .replace('mm', minutes)
}

/**
 * Format a single field value based on its type
 */
export function formatFieldValue(
  fieldValue: any,
  fieldType: FieldType,
  fieldProperty?: IFieldProperty
): string | number | boolean | any[] | Record<string, any> {
  // Handle null/undefined
  if (fieldValue === null || fieldValue === undefined) {
    return ''
  }

  switch (fieldType) {
    case FieldType.Text:
      // Text fields can be simple strings or arrays of segments
      if (Array.isArray(fieldValue)) {
        return fieldValue
          .filter((segment: any) => segment != null)
          .map((segment: any) => segment.text || segment)
          .join('')
      }
      return String(fieldValue)

    case FieldType.Email:
    case FieldType.Url:
    case FieldType.Phone:
      // These fields can be simple strings or arrays of segments
      if (Array.isArray(fieldValue)) {
        return fieldValue
          .filter((segment: any) => segment != null)
          .map((segment: any) => segment.text || segment)
          .join(', ')
      }
      return String(fieldValue)

    case FieldType.Number:
      if (typeof fieldValue === 'number') {
        const formatter = (fieldProperty as any)?.formatter

        if (!formatter) {
          return fieldValue.toString()
        }

        // Apply formatting based on Lark's NumberFormatter setting (Excel-style patterns)

        // Handle percentage formats: 0% or 0.00%
        if (formatter.endsWith('%')) {
          const percentValue = fieldValue * 100
          const decimalMatch = formatter.match(/0\.(0+)%/)
          if (decimalMatch) {
            const decimals = decimalMatch[1].length
            return `${percentValue.toFixed(decimals)}%`
          }
          return `${Math.round(percentValue)}%`
        }

        // Check for thousands separator
        const hasThousandsSeparator = formatter.includes('#,##0')

        // Extract decimal places from pattern (e.g., "0.000000" has 6 decimals)
        const decimalMatch = formatter.match(/\.(0+)/)
        const decimalPlaces = decimalMatch ? decimalMatch[1].length : 0

        // Format the number
        if (hasThousandsSeparator) {
          if (decimalPlaces > 0) {
            return fieldValue.toLocaleString('en-US', {
              minimumFractionDigits: decimalPlaces,
              maximumFractionDigits: decimalPlaces
            })
          } else {
            return Math.round(fieldValue).toLocaleString('en-US')
          }
        } else {
          if (decimalPlaces > 0) {
            return fieldValue.toFixed(decimalPlaces)
          } else if (formatter === '0') {
            return Math.round(fieldValue).toString()
          } else {
            console.warn('Unknown number formatter:', formatter)
            return fieldValue.toString()
          }
        }
      }
      return String(fieldValue)

    case FieldType.Currency:
      if (typeof fieldValue === 'number') {
        const currencyCode = (fieldProperty as any)?.currencyCode || 'CNY'
        const decimalDigits = (fieldProperty as any)?.decimalDigits ?? 2

        // Map currency codes to their locales for proper symbol display
        const currencyLocaleMap: Record<string, string> = {
          'AED': 'ar-AE',
          'AUD': 'en-AU',
          'BRL': 'pt-BR',
          'CAD': 'en-CA',
          'CHF': 'de-CH',
          'CNY': 'zh-CN',
          'EUR': 'de-DE',
          'GBP': 'en-GB',
          'HKD': 'zh-HK',
          'IDR': 'id-ID',
          'INR': 'en-IN',
          'JPY': 'ja-JP',
          'KRW': 'ko-KR',
          'MOP': 'zh-MO',
          'MXN': 'es-MX',
          'MYR': 'ms-MY',
          'PHP': 'en-PH',
          'PLN': 'pl-PL',
          'RUB': 'ru-RU',
          'SGD': 'en-SG',
          'THB': 'th-TH',
          'TRY': 'tr-TR',
          'TWD': 'zh-TW',
          'USD': 'en-US',
          'VND': 'vi-VN',
        }

        const locale = currencyLocaleMap[currencyCode] || 'en-US'

        return fieldValue.toLocaleString(locale, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: decimalDigits,
          maximumFractionDigits: decimalDigits
        })
      }
      return String(fieldValue)

    case FieldType.Progress:
      if (typeof fieldValue === 'number') {
        return `${Math.round(fieldValue * 100)}%`
      }
      return String(fieldValue)

    case FieldType.SingleSelect:
      return (fieldValue as any)?.text || ''

    case FieldType.MultiSelect:
      if (Array.isArray(fieldValue)) {
        return fieldValue
          .filter((v: any) => v != null)
          .map((v: any) => v.text)
          .join(', ')
      }
      return ''

    case FieldType.DateTime:
      if (typeof fieldValue === 'number') {
        const date = new Date(fieldValue)
        // SDK uses camelCase (dateFormat), OpenAPI uses snake_case (date_formatter)
        const prop = fieldProperty as any
        const dateFormat = prop?.dateFormat || prop?.date_formatter || 'yyyy/MM/dd'
        return formatDateWithPattern(date, dateFormat)
      }
      return String(fieldValue)

    case FieldType.Checkbox:
      // Return actual boolean for proper conditional support
      return Boolean(fieldValue)

    case FieldType.User:
    case FieldType.CreatedUser:
    case FieldType.ModifiedUser:
      if (Array.isArray(fieldValue)) {
        return fieldValue
          .filter((u: any) => u != null)
          .map((u: any) => u.name || u.en_name || u.id)
          .join(', ')
      }
      if (typeof fieldValue === 'object' && fieldValue !== null) {
        return fieldValue.name || fieldValue.en_name || fieldValue.id || ''
      }
      return ''

    case FieldType.Attachment:
      // Preserve attachment metadata for image replacement in templates
      // Return the raw array with file_token, name, type, etc.
      if (Array.isArray(fieldValue)) {
        return fieldValue.filter((item: any) => item != null) // Pass through for image processing
      }
      return []

    case FieldType.Formula:
    case FieldType.Barcode:
    case FieldType.Lookup:
    case FieldType.AutoNumber:
    case FieldType.Rating:
      // These fields may return various types
      if (Array.isArray(fieldValue)) {
        // Handle array format (e.g., [{text: "value"}])
        return fieldValue
          .filter((item: any) => item != null)
          .map((item: any) => item.text || item)
          .join('')
      }
      if (typeof fieldValue === 'object' && fieldValue !== null) {
        // If it has a 'text' property, use that
        if ('text' in fieldValue) {
          return fieldValue.text
        }
        // If it has a 'value' property, format that
        if ('value' in fieldValue) {
          return formatFieldValue(fieldValue.value, fieldValue.type || FieldType.Text)
        }
        return ''
      }
      return String(fieldValue)

    case FieldType.CreatedTime:
    case FieldType.ModifiedTime:
      if (typeof fieldValue === 'number') {
        const date = new Date(fieldValue)
        // SDK uses camelCase (dateFormat), OpenAPI uses snake_case (date_formatter)
        const prop = fieldProperty as any
        const dateFormat = prop?.dateFormat || prop?.date_formatter || 'yyyy/MM/dd HH:mm'
        return formatDateWithPattern(date, dateFormat)
      }
      return String(fieldValue)

    case FieldType.Location:
      if (typeof fieldValue === 'object' && fieldValue !== null) {
        return fieldValue.address || fieldValue.name || ''
      }
      return String(fieldValue)

    default:
      // For unknown types, try to stringify
      if (typeof fieldValue === 'object' && fieldValue !== null) {
        if ('text' in fieldValue) return fieldValue.text
        if ('value' in fieldValue) return String(fieldValue.value)
        return ''
      }
      return String(fieldValue)
  }
}

/**
 * Format linked record data (recursively)
 */
export async function formatLinkedRecord(
  linkedRecord: IRecord,
  fieldMetaList: IFieldMeta[],
  currentDepth: number = 0,
  maxDepth: number = 5,
  visitedTables: Set<string> = new Set()
): Promise<Record<string, any>> {
  const formatted: Record<string, any> = {}

  for (const field of fieldMetaList) {
    const fieldValue = linkedRecord.fields[field.id]
    const variableName = sanitizeFieldName(field.name)

    // Handle nested links with depth control
    if ((field.type === FieldType.SingleLink || field.type === FieldType.DuplexLink) &&
        currentDepth < maxDepth &&
        fieldValue &&
        typeof fieldValue === 'object') {

      const linkProperty = field.property as ICommonLinkFieldProperty
      const linkedTableId = (fieldValue as any).tableId || (fieldValue as any).table_id
      const recordIds = (fieldValue as any).recordIds || (fieldValue as any).record_ids || []
      const isMultiple = linkProperty?.multiple === true

      // Prevent circular references
      if (linkedTableId && !visitedTables.has(linkedTableId) && recordIds.length > 0) {
        const newVisitedTables = new Set(visitedTables)
        newVisitedTables.add(linkedTableId)

        try {
          const linkedTable = await bitable.base.getTableById(linkedTableId)
          const linkedFieldMeta = await linkedTable.getFieldMetaList()

          const linkedRecords = []
          for (const recordId of recordIds) {
            try {
              const nestedRecordValue = await linkedTable.getRecordById(recordId)
              const nestedRecord: IRecord = { recordId, fields: nestedRecordValue.fields }
              const nestedData = await formatLinkedRecord(
                nestedRecord,
                linkedFieldMeta,
                currentDepth + 1,
                maxDepth,
                newVisitedTables
              )
              linkedRecords.push(nestedData)
            } catch (err) {
              console.error(`Failed to load nested record ${recordId}:`, err)
            }
          }

          formatted[variableName] = isMultiple ? linkedRecords : linkedRecords[0] || null
        } catch (err) {
          console.error(`Failed to load nested linked field ${field.name}:`, err)
          formatted[variableName] = isMultiple ? [] : null
        }
      } else {
        // Max depth reached or circular reference detected
        formatted[variableName] = isMultiple ? [] : null
      }
    } else {
      // Regular field or max depth reached
      formatted[variableName] = formatFieldValue(fieldValue, field.type, field.property)
    }
  }

  return formatted
}
